import React, { useState, useEffect, useRef } from 'react';
import { AddressInput } from '../ui';
import axiosInstance from '../../utils/axiosConfig';
import '../CRMProfileScreen.css';
import { useUserInfo } from '../../contexts/UserContext';
import { dashboardService } from '../../services/dashboardService';
import { useFlashMessage } from '../FlashMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTimes, faMoneyBillWave, faSearch } from '@fortawesome/free-solid-svg-icons';
import { CURRENCY_OPTIONS } from '../../utils/constants';
import { CRMTabHero, CRMSectionHeader, TabIcons } from './CRMTabUI';
// Phase 3: Removed personioService import - using HMS APIs directly
import ConfirmationPopup from '../ConfirmationPopup';
import BankingVerificationModal from './BankingVerificationModal';
import BankingUploadModal from './BankingUploadModal';
import { confirmName, lookupNames } from '../../services/documentNamingService';
import SuggestedName from '../DocumentNaming/SuggestedName';
import useSuggestionPolling from '../DocumentNaming/useSuggestionPolling';
import BankingAIAnalysisModal from './BankingAIAnalysisModal';
import { getErrorMessage } from '../../utils/errorMessages';
import { fetchPaymentTermOptions, paymentTermLabel } from '../../services/paymentTermsService';
import { DocumentReadingRunPanel, runOutcome, useDocumentReadingRun } from './documentReadingRun';

const CRMBankingDetailsTab = ({ profileData, onDataChange, isNewProfile }) => {
    const { userInfo } = useUserInfo();
    const { showError, showSuccess, showInfo } = useFlashMessage();
    const [currentUserInfo, setCurrentUserInfo] = useState(null);

    const [formData, setFormData] = useState({
        banking_details_profiles: [],
        payment_terms_debtor_selections: [],
        payment_terms_creditor_selections: [],
        vat_terms_selections: [],
        uid_registration_number: '',
        lei_registration_number: '',
        lic_registration_number: '',
        vat_registration_number: '',
        duns_registration_number: '',
        verified_banking_details: false,
        base_currency: 'USD',
        additional_currencies: [],
        ...profileData
    });

    const [showPaymentDebtorDropdown, setShowPaymentDebtorDropdown] = useState(false);
    const [showPaymentCreditorDropdown, setShowPaymentCreditorDropdown] = useState(false);
    const [showVATDropdown, setShowVATDropdown] = useState(false);
    
    // File upload state
    const [uploading, setUploading] = useState(false);
    
    // Document management state - v2 DocuWare integration
    const [documentList, setDocumentList] = useState([]);
    const [bankingDisplayNames, setBankingDisplayNames] = useState({});
    useSuggestionPolling(bankingDisplayNames, setBankingDisplayNames, 'banking_verification');
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [documentError, setDocumentError] = useState(null);
    const [showDocuments, setShowDocuments] = useState(false);
    
    // Employee information state for document metadata
    const [employeeInfoCache, setEmployeeInfoCache] = useState({}); // Cache for employee info by user_id
    
    // Audit logs state
    const [auditLogs, setAuditLogs] = useState([]);
    const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
    const [allEmployees, setAllEmployees] = useState([]); // Personio employee list for lookup
    
    const paymentDebtorRef = useRef(null);
    const paymentCreditorRef = useRef(null);
    const vatTermsRef = useRef(null);

    // Delete confirmation state
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    
    // Duplicate detection state
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateFileInfo, setDuplicateFileInfo] = useState(null);
    
    // Upload modal state
    const [showUploadModal, setShowUploadModal] = useState(false);
    
    // Banking verification state
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [submittingBanking, setSubmittingBanking] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [verificationAnalysisData, setVerificationAnalysisData] = useState(null);
    
    // AI Analysis Modal state
    const [showAIAnalysisModal, setShowAIAnalysisModal] = useState(false);
    const [selectedAIAnalysis, setSelectedAIAnalysis] = useState(null);

    // Payment terms come from the invoicing PaymentTerms catalogue (CRM-gated
    // endpoint), not a list in this file. Options are {value: term code,
    // label, days_until_due}; the stored selection is the CODE.
    const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
    const [paymentTermsLoadFailed, setPaymentTermsLoadFailed] = useState(false);

    const vatTermsOptions = [
        'Standard VAT',
        'Zero-rated',
        'Exempt',
        'Reverse Charge'
    ];

    // An empty picker looks identical to a broken one, so a failed load is
    // recorded and surfaced in the dropdown rather than rendering nothing.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const options = await fetchPaymentTermOptions();
                if (cancelled) return;
                setPaymentTermsOptions(options);
                setPaymentTermsLoadFailed(options.length === 0);
            } catch (error) {
                if (cancelled) return;
                console.error('Failed to load payment terms options:', error);
                setPaymentTermsOptions([]);
                setPaymentTermsLoadFailed(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (profileData) {
            console.log('🏦 CRMBankingDetailsTab: ProfileData changed, updating formData:', {
                incoming_banking_details_length: profileData.banking_details_profiles?.length || 0,
                incoming_banking_details: profileData.banking_details_profiles
            });
            
            setFormData(prevFormData => {
                // If this is the initial load (no previous form data exists)
                const isInitialLoad = !prevFormData || Object.keys(prevFormData).length === 0;
                
                if (isInitialLoad) {
                    console.log('🏦 CRMBankingDetailsTab: Initial load - using server data');
                    return {
                        banking_details_profiles: [],
                        payment_terms_debtor_selections: [],
                        payment_terms_creditor_selections: [],
                        vat_terms_selections: [],
                        uid_registration_number: '',
                        lei_registration_number: '',
                        lic_registration_number: '',
                        vat_registration_number: '',
                        duns_registration_number: '',
                        verified_banking_details: false,
                        ...profileData
                    };
                } else {
                    // For subsequent updates, only update if the server has more recent data
                    // or if our local banking details are empty but server has data
                    const localBankingDetails = prevFormData.banking_details_profiles || [];
                    const serverBankingDetails = profileData.banking_details_profiles || [];
                    
                    console.log('🏦 CRMBankingDetailsTab: Subsequent update - comparing data:', {
                        localCount: localBankingDetails.length,
                        serverCount: serverBankingDetails.length,
                        localData: localBankingDetails,
                        serverData: serverBankingDetails
                    });
                    
                    // Preserve local banking details if we have them and server doesn't have more
                    const bankingDetailsToUse = (localBankingDetails.length > 0 && serverBankingDetails.length === 0) 
                        ? localBankingDetails 
                        : serverBankingDetails;
                    
                    console.log('🏦 CRMBankingDetailsTab: Using banking details:', {
                        source: bankingDetailsToUse === localBankingDetails ? 'local' : 'server',
                        count: bankingDetailsToUse.length,
                        data: bankingDetailsToUse
                    });
                    
                    return {
                        ...prevFormData,
                        ...profileData,
                        banking_details_profiles: bankingDetailsToUse
                    };
                }
            });
            
            console.log('🏦 CRMBankingDetailsTab: FormData update complete');
        }
    }, [profileData]);

    // Handle extracted banking details from document analysis
    useEffect(() => {
        if (profileData?.extracted_banking_details && Array.isArray(profileData.extracted_banking_details)) {
            console.log('🏦 CRMBankingDetailsTab: Extracted banking details detected:', profileData.extracted_banking_details);

            // A record is a duplicate of an existing one when any non-empty identifier matches:
            // IBAN, account number, or (SWIFT + bank name). Handles SWIFT-only accounts with no IBAN.
            const isDuplicateBanking = (existing, extracted) => {
                if (existing.iban && extracted.iban && existing.iban === extracted.iban) {
                    return true;
                }
                if (existing.account_number && extracted.account_number && existing.account_number === extracted.account_number) {
                    return true;
                }
                if (existing.swift && extracted.swift && existing.swift === extracted.swift &&
                    existing.bank_name && extracted.bank_name && existing.bank_name === extracted.bank_name) {
                    return true;
                }
                return false;
            };

            // Add extracted banking details to the banking_details_profiles array
            setFormData(prev => {
                const currentBanking = prev.banking_details_profiles || [];
                const extractedBanking = profileData.extracted_banking_details;
                
                // Merge extracted banking details (avoid duplicates)
                const newBanking = [...currentBanking];
                extractedBanking.forEach(extracted => {
                    // Check if this extracted item is already in the list
                    const isDuplicate = newBanking.some(existing => isDuplicateBanking(existing, extracted));

                    if (!isDuplicate) {
                        newBanking.push(extracted);
                    }
                });
                
                console.log('🏦 CRMBankingDetailsTab: Added extracted banking details:', {
                    before: currentBanking.length,
                    after: newBanking.length,
                    added: newBanking.length - currentBanking.length
                });
                
                return {
                    ...prev,
                    banking_details_profiles: newBanking,
                    extracted_banking_details: undefined // Clear after processing
                };
            });
            
            // Update parent component
            const updatedData = {
                ...formData,
                banking_details_profiles: [
                    ...(formData.banking_details_profiles || []),
                    ...profileData.extracted_banking_details.filter(extracted => {
                        const isDuplicate = (formData.banking_details_profiles || []).some(existing => isDuplicateBanking(existing, extracted));
                        return !isDuplicate;
                    })
                ],
                extracted_banking_details: undefined
            };
            onDataChange('banking_details_profiles', updatedData.banking_details_profiles);
            
            showSuccess(`${profileData.extracted_banking_details.length} banking detail(s) added from document analysis`, 4000);
        }
    }, [profileData?.extracted_banking_details]);

    // Get current user information for employee_id
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const userInfoData = await dashboardService.getUserInfo();
                setCurrentUserInfo(userInfoData);
            } catch (error) {
                console.error('Error fetching user info:', error);
                // Fallback to userInfo from context
                setCurrentUserInfo(userInfo);
            }
        };

        if (!currentUserInfo && userInfo?.user_id) {
            setCurrentUserInfo(userInfo);
        } else if (!currentUserInfo) {
            fetchUserInfo();
        }
    }, [userInfo, currentUserInfo]);
    
    // Fetch all employees data for employee lookup on document metadata
    useEffect(() => {
        const fetchAllEmployees = async () => {
            try {
                console.log('CRMBankingDetailsTab: Fetching all employees data for document metadata (HMS API)');
                
                // Use HMS-native employees list API
                const response = await axiosInstance.get('/api/employees/hms-employees/');
                
                if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                    setAllEmployees(response.data);
                    console.log('CRMBankingDetailsTab: Loaded employee data from HMS API:', response.data.length, 'employees');
                } else {
                    console.warn('CRMBankingDetailsTab: No employee data received from HMS API');
                }
            } catch (error) {
                console.error('CRMBankingDetailsTab: Error fetching employee data from HMS API:', error);
                // Fallback: try to use empty array to prevent crashes
                setAllEmployees([]);
            }
        };

        fetchAllEmployees();
    }, []); // Run once on component mount
    
    // Fetch audit logs when profile data changes
    useEffect(() => {
        if (profileData?.id) {
            fetchAuditLogs();
        }
    }, [profileData?.id, profileData?.manual_verification_date, profileData?.deactivation_date]);
    
    // Function to get employee info by Django user ID
    const getEmployeeInfoByUserId = async (djangoUserId) => {
        if (!djangoUserId) return null;
        
        // Check cache first
        if (employeeInfoCache[djangoUserId]) {
            console.log(`CRMBankingDetailsTab: Using cached employee info for user ID ${djangoUserId}`);
            return employeeInfoCache[djangoUserId];
        }
        
        try {
            console.log(`CRMBankingDetailsTab: Looking up employee info for Django user ID: ${djangoUserId}`);
            
            // For now, we'll use the Dashboard API to get user info by the current user
            // In a future enhancement, we could create a backend endpoint to lookup any user by ID
            // But for documents uploaded by the current user, we can use their own info
            if (currentUserInfo && currentUserInfo.user_id === parseInt(djangoUserId)) {
                const employeeInfo = {
                    first_name: currentUserInfo.first_name,
                    last_name: currentUserInfo.last_name,
                    position: currentUserInfo.position,
                    department: currentUserInfo.department,
                    office: currentUserInfo.office,
                    employee_id: currentUserInfo.employee_id
                };
                
                // Cache the result
                setEmployeeInfoCache(prev => ({
                    ...prev,
                    [djangoUserId]: employeeInfo
                }));
                
                console.log(`CRMBankingDetailsTab: Found employee info for user ${djangoUserId}:`, employeeInfo);
                return employeeInfo;
            } else {
                // For other users, we'd need a backend endpoint to fetch user info by ID
                // For now, return basic info indicating we can't fetch other users' data
                const placeholderInfo = {
                    first_name: 'User',
                    last_name: `ID ${djangoUserId}`,
                    position: 'Unknown',
                    department: 'Unknown',
                    office: 'Unknown',
                    employee_id: djangoUserId
                };
                
                console.log(`CRMBankingDetailsTab: Cannot fetch info for other user ID ${djangoUserId}, using placeholder`);
                return placeholderInfo;
            }
        } catch (error) {
            console.error(`CRMBankingDetailsTab: Error fetching employee info for user ID ${djangoUserId}:`, error);
            return null;
        }
    };

    // Duplicate detection helper functions
    const checkForDuplicateFilename = (filename) => {
        console.log('🔍 FRONTEND: Checking for duplicate filename:', filename);
        console.log('🔍 FRONTEND: Current documents:', documentList);
        
        if (!documentList || documentList.length === 0) {
            console.log('✅ FRONTEND: No existing documents, no duplicates found');
            return { isDuplicate: false, existingDocument: null };
        }
        
        // Check if any existing document has the same filename in SUBJECT field
        const duplicateDocument = documentList.find(doc => {
            const docInfo = getDocumentInfo(doc);
            const isDup = docInfo.filename === filename;
            console.log(`🔍 FRONTEND: Comparing "${docInfo.filename}" with "${filename}": ${isDup}`);
            return isDup;
        });
        
        if (duplicateDocument) {
            console.log('⚠️ FRONTEND: Duplicate found:', duplicateDocument);
            return { isDuplicate: true, existingDocument: duplicateDocument };
        }
        
        console.log('✅ FRONTEND: No duplicates found');
        return { isDuplicate: false, existingDocument: null };
    };

    const generateIncrementedFilename = (originalFilename) => {
        console.log('🔢 FRONTEND: Generating incremented filename for:', originalFilename);
        
        if (!documentList || documentList.length === 0) {
            console.log('✅ FRONTEND: No existing documents, returning original filename');
            return originalFilename;
        }
        
        // Extract existing filenames from document list
        const existingFilenames = documentList.map(doc => {
            const docInfo = getDocumentInfo(doc);
            return docInfo.filename;
        }).filter(Boolean); // Remove any null/undefined values
        
        console.log('📋 FRONTEND: Existing filenames:', existingFilenames);
        
        // Parse the original filename to extract name and extension
        const lastDotIndex = originalFilename.lastIndexOf('.');
        const nameWithoutExt = lastDotIndex !== -1 ? originalFilename.substring(0, lastDotIndex) : originalFilename;
        const extension = lastDotIndex !== -1 ? originalFilename.substring(lastDotIndex) : '';
        
        console.log('📝 FRONTEND: Name without extension:', nameWithoutExt);
        console.log('📝 FRONTEND: Extension:', extension);
        
        // Start checking from (1) and increment until we find a unique name
        let counter = 1;
        let newFilename = `${nameWithoutExt} (${counter})${extension}`;
        
        while (existingFilenames.includes(newFilename)) {
            counter++;
            newFilename = `${nameWithoutExt} (${counter})${extension}`;
            console.log(`🔢 FRONTEND: Trying counter ${counter}: ${newFilename}`);
        }
        
        console.log('✅ FRONTEND: Generated unique filename:', newFilename);
        return newFilename;
    };

    // Handle outside clicks for dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (paymentDebtorRef.current && !paymentDebtorRef.current.contains(event.target)) {
                setShowPaymentDebtorDropdown(false);
            }
            if (paymentCreditorRef.current && !paymentCreditorRef.current.contains(event.target)) {
                setShowPaymentCreditorDropdown(false);
            }
            if (vatTermsRef.current && !vatTermsRef.current.contains(event.target)) {
                setShowVATDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputChange = (field, value) => {
        const updatedData = {
            ...formData,
            [field]: value
        };
        setFormData(updatedData);
        
        // DEBUG: Log banking details updates specifically
        if (field === 'banking_details_profiles') {
            console.log('🏦 CRMBankingDetailsTab: Banking details updated:', {
                field,
                valueLength: value?.length || 0,
                value: JSON.stringify(value, null, 2),
                fullUpdatedData: updatedData
            });
        }
        
        onDataChange(field, updatedData[field]);
        
        // DEBUG: Confirm data was sent to parent
        if (field === 'banking_details_profiles') {
            console.log('🏦 CRMBankingDetailsTab: Sent banking details to parent via onDataChange');
        }
    };

    const handleMultiSelectToggle = (field, option) => {
        const currentSelections = formData[field] || [];
        const updatedSelections = currentSelections.includes(option)
            ? currentSelections.filter(item => item !== option)
            : [...currentSelections, option];
        
        handleInputChange(field, updatedSelections);
    };

    const removeTag = (field, tagToRemove) => {
        const currentSelections = formData[field] || [];
        const updatedSelections = currentSelections.filter(item => item !== tagToRemove);
        handleInputChange(field, updatedSelections);
    };

    // Bank Details Management
    const addBankDetails = () => {
        const currentBankDetails = formData.banking_details_profiles || [];
        const newBankProfile = {
            id: Date.now(),
            account_owner: '',
            bank_address_data: {},
            account_number: '',
            iban: '',
            swift: '',
            bic: ''
        };
        console.log('🏦 CRMBankingDetailsTab: Adding new bank details:', {
            currentCount: currentBankDetails.length,
            newProfile: newBankProfile
        });
        handleInputChange('banking_details_profiles', [...currentBankDetails, newBankProfile]);
    };

    const removeBankDetails = (bankId) => {
        const currentBankDetails = formData.banking_details_profiles || [];
        const bankToRemove = currentBankDetails.find(bank => bank.id === bankId);
        
        // Check if this is a new unsaved profile (created with Date.now()) vs saved profile
        const isNewUnsavedProfile = bankToRemove && (
            // New profiles have Date.now() IDs (13+ digits) or no database ID
            (typeof bankToRemove.id === 'number' && bankToRemove.id.toString().length >= 13) ||
            !bankToRemove.database_id
        );
        
        if (isNewUnsavedProfile) {
            // Allow deletion of new unsaved profiles
            console.log('🗑️ Removing NEW unsaved banking profile:', bankId);
            const updatedBankDetails = currentBankDetails.filter(bank => bank.id !== bankId);
            handleInputChange('banking_details_profiles', updatedBankDetails);
        } else {
            // Prevent deletion of saved profiles - show error message
            console.log('🚫 Cannot delete SAVED banking profile:', bankId);
            showError('Cannot delete saved banking details. Use the "Banking Details Status" section at the top to archive all banking information if needed.');
        }
    };

    const handleBankDetailsChange = (bankId, field, value) => {
        console.log('🏦 CRMBankingDetailsTab: Bank detail field changed:', {
            bankId,
            field,
            value,
            currentBankingCount: formData.banking_details_profiles?.length || 0
        });
        
        const currentBankDetails = formData.banking_details_profiles || [];
        const updatedBankDetails = currentBankDetails.map(bank => 
            bank.id === bankId ? { ...bank, [field]: value } : bank
        );
        
        console.log('🏦 CRMBankingDetailsTab: Updated banking details:', {
            updatedCount: updatedBankDetails.length,
            updatedBankDetails: JSON.stringify(updatedBankDetails, null, 2)
        });
        
        handleInputChange('banking_details_profiles', updatedBankDetails);
    };

    const handleBankAddressSelect = (bankId, suggestion) => {
        const addressData = {
            formatted_address: suggestion.label,
            geometry: suggestion.geometry,
            place_name: suggestion.label,
            center: suggestion.center
        };
        handleBankDetailsChange(bankId, 'bank_address_data', addressData);
    };

    // Handle modal file upload - receives file directly from modal
    const handleModalFileUpload = async (file, namingPayload = null) => {
        if (!file) return;

        // Check if profile has been saved first - NEW VALIDATION
        if (isNewProfile || !profileData?.id) {
            showError('Please save the partner profile first before uploading documents. Documents require a valid partner ID.');
            return;
        }
        // Stash the AI naming payload on the file ref so the duplicate
        // handlers and performFileUpload can reach it.
        file._namingPayload = namingPayload;

        // Check file size limit (30MB)
        const maxSizeInBytes = 30 * 1024 * 1024; // 30MB
        if (file.size > maxSizeInBytes) {
            showError(`File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds the 30MB limit. Please choose a smaller file.`);
            return;
        }

        console.log('📤 FRONTEND: Starting banking document upload process');
        console.log('📄 FRONTEND: File details:', { name: file.name, size: file.size, type: file.type });
        
        // Check for duplicate filename before proceeding with upload
        const duplicateCheck = checkForDuplicateFilename(file.name);
        
        if (duplicateCheck.isDuplicate) {
            console.log('⚠️ FRONTEND: Duplicate detected, showing modal');
            // Store the upload details for later processing
            setDuplicateFileInfo({
                file,
                existingDocument: duplicateCheck.existingDocument
            });
            setShowDuplicateModal(true);
            return; // Stop here and wait for user decision
        }
        
        // No duplicate found, proceed with normal upload
        console.log('✅ FRONTEND: No duplicate found, proceeding with upload');
        await performFileUpload(file, file.name);
    };

    // Separate function to handle the actual upload process
    const performFileUpload = async (file, finalFilename) => {
        console.log('🚀 FRONTEND: Performing banking document upload with filename:', finalFilename);
        
        setUploading(true);

        try {
            // Validate required data - this check is now redundant but kept for safety
            if (!profileData?.id) {
                throw new Error('Partner ID is required. Please save the profile first.');
            }

            if (!currentUserInfo?.user_id && !currentUserInfo?.employee_id) {
                throw new Error('Unable to identify current user. Please refresh the page and try again.');
            }

            // Use user_id (Django user ID) for DocuWare employee_id field
            const employeeId = currentUserInfo?.user_id || '1';
            const partnerName = profileData?.legal_entity_name || 'Unknown Partner';
            const subject = finalFilename;  // Use the final filename (potentially renamed) as subject
            
            console.log('Two-step upload started:', {
                profileData_id: profileData?.id,
                partnerName: partnerName,
                employeeId: employeeId,
                fileName: file.name,
                fileSize: file.size
            });

            // Step 1: Create document record with metadata
            console.log('Step 1: Creating document record...');
            const recordData = {
                partner_name: partnerName,
                employee_id: employeeId,
                partner_id: profileData?.id.toString(),
                subject: subject
            };

            const recordResponse = await axiosInstance.post('/api/docuware/banking-verification/create-record/', recordData, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!recordResponse.data.success || !recordResponse.data.document_id) {
                throw new Error('Failed to create document record: ' + (recordResponse.data.error || 'No document ID returned'));
            }

            const documentId = recordResponse.data.document_id;
            console.log('Step 1 completed - Document ID:', documentId);

            // Step 2: Upload file to the created document
            console.log('Step 2: Uploading file to document...');
            const fileFormData = new FormData();
            fileFormData.append('file', file);

            const fileResponse = await axiosInstance.post(`/api/docuware/banking-verification/upload-file/${documentId}/`, fileFormData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            console.log('Step 2 completed - File uploaded successfully');
            
            // Persist the AI display name. The file upload already succeeded,
            // so a failure here must NOT block the success flow — surface a
            // warning toast and continue.
            const namingPayload = file?._namingPayload;
            if (namingPayload && documentId) {
                try {
                    await confirmName({
                        docuwareDocumentId: documentId,
                        module: 'banking_verification',
                        file,
                        typeDropdownValue: namingPayload.typeDropdownValue || '',
                        partnerName: namingPayload.partnerName || '',
                        kycSubtype: namingPayload.kycSubtype || '',
                        relatedEntityId: profileData?.id ? String(profileData.id) : '',
                        originalFilename: file?.name || finalFilename,
                        components: namingPayload.components || {},
                        aiSuggestion: namingPayload.aiSuggestion || {},
                        userOverrides: namingPayload.userOverrides || [],
                        extractionConfidence: namingPayload.extractionConfidence ?? null,
                    });
                } catch (err) {
                    console.warn('AI display name persist failed (non-blocking):', err);
                    showError('Document uploaded, but the AI name could not be saved — you can rename it in the Document Hub.');
                }
            }
            
            const uploadResult = {
                success: fileResponse.data.success || true,
                status: fileResponse.status,
                data: {
                    ...fileResponse.data,
                    document_id: documentId,
                    message: 'Document uploaded successfully using two-step process'
                },
                fileName: file.name
            };
            
            // Show success message and refresh document list after successful upload
            if (uploadResult.success) {
                showSuccess(`Banking document "${finalFilename}" uploaded successfully!`);
                if (showDocuments) {
                    setTimeout(() => {
                        fetchBankingDocuments();
                    }, 1000);
                }
            }

        } catch (error) {
            console.error('Two-step upload error:', error);
            
            // Show error message via FlashMessage
            showError(getErrorMessage(error, 'Banking details operation failed. Please try again.'));
            
            // Handle axios error response
            if (error.response) {
                const errorData = error.response.data || {};
            } else if (error.request) {
                // Request was made but no response received
            } else {
                // Something else happened
            }
        } finally {
            // Always reset uploading state
            setUploading(false);
        }
    };

    // Duplicate modal handlers
    const handleDuplicateKeepBoth = async () => {
        console.log('👥 FRONTEND: User chose to keep both files');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate file info available');
            return;
        }
        
        const { file } = duplicateFileInfo;
        const incrementedFilename = generateIncrementedFilename(file.name);
        
        console.log('📝 FRONTEND: Renaming file to:', incrementedFilename);
        
        // Close modal and proceed with upload using incremented filename
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
        
        await performFileUpload(file, incrementedFilename);
    };

    const handleDuplicateReplace = async () => {
        console.log('🔄 FRONTEND: User chose to replace existing file');
        
        if (!duplicateFileInfo) {
            console.error('❌ FRONTEND: No duplicate file info available');
            return;
        }
        
        const { file, existingDocument } = duplicateFileInfo;
        
        try {
            console.log('🗑️ FRONTEND: Deleting existing document:', existingDocument.Id);
            
            // Delete the existing document first
            await axiosInstance.delete(`/api/docuware/banking-verification/delete/${existingDocument.Id}/`);
            
            console.log('✅ FRONTEND: Existing document deleted successfully');
            
            // Close modal and proceed with upload using original filename
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
            
            await performFileUpload(file, file.name);
            
        } catch (error) {
            console.error('❌ FRONTEND: Error deleting existing document:', error);
            showError(getErrorMessage(error, 'Banking details operation failed. Please try again.'));
            
            // Close modal even if deletion failed
            setShowDuplicateModal(false);
            setDuplicateFileInfo(null);
        }
    };

    const handleDuplicateCancel = () => {
        console.log('❌ FRONTEND: User cancelled upload due to duplicate');
        
        // Simply close the modal and reset state
        setShowDuplicateModal(false);
        setDuplicateFileInfo(null);
    };

    // Fetch banking verification documents from DocuWare v2 API
    const fetchBankingDocuments = async () => {
        if (!profileData?.id) return;
        
        setLoadingDocuments(true);
        setDocumentError(null);
        
        try {
            console.log('CRMBankingDetailsTab: Fetching banking documents for partner ID:', profileData.id);
            
            const response = await axiosInstance.get('/api/docuware/banking-verification/documents/', {
                params: {
                    partner_id: profileData.id,
                    count: 50
                }
            });
            
            const documents = response.data.Items || [];
            console.log('CRMBankingDetailsTab: Raw documents received from API:', documents);
            
            // Log detailed document information for debugging
            documents.forEach((doc, index) => {
                console.log(`Document ${index + 1} (ID: ${doc.Id}):`, {
                    fullDocument: doc,
                    fields: doc.Fields,
                    fileSize: doc.FileSize
                });
                
                // Specifically log date-related fields
                if (doc.Fields) {
                    const dateField = doc.Fields.find(f => f.FieldName === 'DATE_UPLOADED');
                    console.log(`Document ${index + 1} DATE_UPLOADED field:`, {
                        found: !!dateField,
                        fieldData: dateField,
                        itemValue: dateField?.Item,
                        itemType: typeof dateField?.Item
                    });
                }
            });
            
            setDocumentList(documents);
            console.log('CRMBankingDetailsTab: Successfully fetched', documents.length, 'documents');

            // Bulk lookup AI display names; fall back silently to SUBJECT
            // for legacy documents that have no row in document_naming.
            try {
                const ids = documents.map(d => d?.Id).filter(Boolean).map(String);
                const map = await lookupNames(ids, 'banking_verification');
                setBankingDisplayNames(map || {});
            } catch (lookupErr) {
                console.warn('AI display name lookup failed (non-blocking):', lookupErr);
            }
            
        } catch (error) {
            console.error('CRMBankingDetailsTab: Error fetching banking documents:', error);
            setDocumentError(getErrorMessage(error, 'Banking details operation failed. Please try again.'));
        } finally {
            setLoadingDocuments(false);
        }
    };

    // Download banking document
    const downloadDocument = async (documentId, filename) => {
        try {
            const response = await axiosInstance.get(
                `/api/docuware/banking-verification/download/${documentId}/`,
                { responseType: 'blob' }
            );
            
            // Extract filename from Content-Disposition header if available, otherwise use provided filename
            let downloadFilename = filename;
            const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    downloadFilename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            // If we still don't have a good filename, create one with document ID and appropriate extension
            if (!downloadFilename || downloadFilename === `Banking_Document_${documentId}`) {
                const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
                let extension = '';
                if (contentType.includes('pdf')) {
                    extension = '.pdf';
                } else if (contentType.includes('image')) {
                    extension = '.jpg';
                } else if (contentType.includes('document') || contentType.includes('word')) {
                    extension = '.doc';
                }
                downloadFilename = `Banking_Document_${documentId}${extension}`;
            }
            
            // Create blob URL and trigger download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log(`Downloaded document ${documentId} as "${downloadFilename}"`);
            
        } catch (error) {
            console.error('Error downloading document:', error);
            showError(getErrorMessage(error, 'Banking details operation failed. Please try again.'));
        }
    };

    // Delete banking document
    const deleteDocument = async (documentId, filename) => {
        try {
            await axiosInstance.delete(`/api/docuware/banking-verification/delete/${documentId}/`);
            
            // Refresh document list
            fetchBankingDocuments();
            
            // Show success message via FlashMessage
            showSuccess(`Document "${filename}" deleted successfully`);
            
        } catch (error) {
            console.error('Error deleting document:', error);
            showError(getErrorMessage(error, 'Banking details operation failed. Please try again.'));
        }
    };

    // Extract document metadata for display
    const getDocumentInfo = (document) => {
        console.log('getDocumentInfo: Processing document:', {
            documentId: document.Id,
            fullDocument: document,
            fieldsArray: document.Fields
        });
        
        const fields = document.Fields || [];
        const getFieldValue = (fieldName) => {
            const field = fields.find(f => f.FieldName === fieldName);
            const value = field?.Item || '';
            console.log(`getDocumentInfo: Field ${fieldName}:`, {
                found: !!field,
                fieldObject: field,
                extractedValue: value,
                valueType: typeof value
            });
            return value;
        };
        
        // Try to get actual filename - DocuWare stores subject, which often contains the filename
        // Priority: 1. SUBJECT field (often contains filename), 2. Original filename from upload, 3. Fallback
        const subject = getFieldValue('SUBJECT');
        const originalFilename = subject && subject !== 'Banking Verification Document' ? subject : null;
        const fallbackFilename = `document_${document.Id}.pdf`;
        const filename = originalFilename || fallbackFilename;
        
        // Get the date uploaded and employee ID for fetching user info
        const dateUploaded = getFieldValue('DATE_UPLOADED');
        const employeeId = getFieldValue('EMPLOYEE_ID'); // This is Django user_id, need to convert to Personio ID
        
        console.log('getDocumentInfo: Date extraction complete:', {
            rawDate: dateUploaded,
            filename: filename,
            employeeId: employeeId,
            employeeIdType: typeof employeeId
        });
        
        const nameRow = bankingDisplayNames?.[String(document.Id)] || null;
        const aiDisplayName = nameRow?.display_name || null;
        
        return {
            filename,
            displayName: aiDisplayName || filename,
            nameRow,
            onNameRow: (updated) => setBankingDisplayNames((prev) => ({ ...(prev || {}), [String(document.Id)]: updated })),
            aiDisplayName,
            dateUploaded,
            fileSize: document.FileSize || 0,
            documentId: document.Id,
            employeeId: employeeId // Store employee ID for fetching user info
        };
    };

    // Format file size for display
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Format date for display - improved to handle DocuWare ISO dates and .NET JSON dates
    const formatDate = (dateString) => {
        console.log('formatDate: Input received:', {
            value: dateString,
            type: typeof dateString,
            isNull: dateString === null,
            isUndefined: dateString === undefined,
            isEmpty: dateString === '',
            stringValue: String(dateString)
        });
        
        if (!dateString) {
            console.log('formatDate: No date string provided, returning "Unknown"');
            return 'Unknown';
        }
        
        try {
            let date;
            let cleanDateString;
            
            if (typeof dateString === 'string') {
                // Check if it's a .NET JSON date format: /Date(1751569475249)/
                const netDateMatch = dateString.match(/\/Date\((\d+)\)\//);
                if (netDateMatch) {
                    const timestamp = parseInt(netDateMatch[1], 10);
                    console.log('formatDate: .NET JSON date detected:', {
                        original: dateString,
                        extractedTimestamp: timestamp,
                        timestampType: typeof timestamp
                    });
                    date = new Date(timestamp);
                } else {
                    // Handle regular ISO format dates (with Z suffix)
                    cleanDateString = dateString.replace(/Z+$/, 'Z').trim();
                    console.log('formatDate: ISO date format, cleaned:', {
                        original: dateString,
                        cleaned: cleanDateString
                    });
                    date = new Date(cleanDateString);
                }
            } else {
                console.log('formatDate: Non-string date value, attempting direct conversion');
                date = new Date(dateString);
            }
            
            console.log('formatDate: Date object created:', {
                dateObject: date,
                getTime: date.getTime(),
                isValid: !isNaN(date.getTime()),
                toISOString: isNaN(date.getTime()) ? 'INVALID' : date.toISOString()
            });
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.error('formatDate: Invalid date detected:', {
                    input: dateString,
                    cleaned: cleanDateString,
                    dateObject: date
                });
                return 'Invalid Date';
            }
            
            // Return formatted date in local format
            const formattedDate = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            console.log('formatDate: Successfully formatted date:', {
                input: dateString,
                output: formattedDate,
                dateObject: date
            });
            
            return formattedDate;
        } catch (error) {
            console.error('formatDate: Exception during date parsing:', {
                error: error,
                errorMessage: error.message,
                input: dateString,
                stack: error.stack
            });
            return 'Invalid Date';
        }
    };

    // Load documents when partner changes or component mounts
    useEffect(() => {
        if (profileData?.id && showDocuments) {
            fetchBankingDocuments();
        }
    }, [profileData?.id, showDocuments]);

    // Auto-fetch documents when banking tab becomes active - NEW FUNCTIONALITY
    // This will run when the component first mounts (tab becomes active)
    useEffect(() => {
        console.log('CRMBankingDetailsTab: Component mounted, checking if documents should be auto-fetched');
        if (profileData?.id) {
            console.log(`CRMBankingDetailsTab: Partner ID ${profileData.id} available, auto-fetching banking documents`);
            fetchBankingDocuments();
            // Also show the documents section automatically since we're loading them
            setShowDocuments(true);
        } else {
            console.log('CRMBankingDetailsTab: No partner ID available, skipping auto-fetch');
        }
    }, [profileData?.id]); // Runs when component mounts and when profileData.id changes

    // Banking verification functions

    // The verification runs in the background (reading a scanned document takes
    // minutes). Success is only announced when a verdict was saved AND every
    // chosen document was read; otherwise the tab says which were not and why.
    const handleBankingRunFinished = (finishedRun) => {
        if (finishedRun.status === 'done') {
            fetchAuditLogs();
            setSelectedFiles([]);
        }
        const outcome = runOutcome(
            finishedRun,
            'Banking verification analysis completed successfully! Check the audit trail to view details.'
        );
        if (outcome.tone === 'success') showSuccess(outcome.text);
        else if (outcome.tone === 'warning') showInfo(outcome.text, 10000);
        else showError(outcome.text, 10000);
    };
    const {
        run: bankingRun,
        active: bankingRunActive,
        start: startBankingRun,
        clear: clearBankingRun,
    } = useDocumentReadingRun(handleBankingRunFinished, { kind: 'banking_verification', partnerId: profileData?.id });
    const analyzingBanking = submittingBanking || bankingRunActive;

    const runBankingVerification = async () => {
        if (selectedFiles.length === 0) {
            showError('Please select at least one document for banking verification');
            return;
        }

        console.log('FRONTEND: Starting banking verification analysis');
        console.log(`Partner ID: ${profileData?.id}`);
        console.log(`Selected files count: ${selectedFiles.length}`);

        setSubmittingBanking(true);
        clearBankingRun();

        try {

            // Get manual banking details from current form data
            const manualBankingDetails = {
                banking_details_profiles: formData.banking_details_profiles || [],
                payment_terms_debtor: formData.payment_terms_debtor_selections || [],
                payment_terms_creditor: formData.payment_terms_creditor_selections || [],
                vat_terms: formData.vat_terms_selections || [],
                tax_registration_numbers: {
                    uid: formData.uid_registration_number || '',
                    lei: formData.lei_registration_number || '',
                    lic: formData.lic_registration_number || '',
                    vat: formData.vat_registration_number || '',
                    duns: formData.duns_registration_number || ''
                }
            };

            // Map selected files for backend
            const mappedFiles = selectedFiles.map(file => ({
                document_id: file.document_id,
                filename: file.filename
            }));
            
            const requestPayload = {
                selected_documents: mappedFiles,
                manual_banking_details: manualBankingDetails
            };

            // Answers straight away with a queued run; the verdict arrives through
            // handleBankingRunFinished once the background service has finished.
            const response = await axiosInstance.post(
                `/api/crm/partners/${profileData.id}/banking-verification/analyze/`,
                requestPayload
            );
            startBankingRun(response.data);
        } catch (error) {
            console.error('ERROR: Error starting banking verification:', error);
            showError(getErrorMessage(error, 'Banking verification could not be started. Please try again.'));
        } finally {
            setSubmittingBanking(false);
        }
    };

    const acceptVerificationResult = async () => {
        if (!verificationAnalysisData?.analysis_id) return;
        
        try {
            const response = await axiosInstance.post(
                `/api/crm/partners/${profileData.id}/banking-verification/${verificationAnalysisData.analysis_id}/accept/`
            );
            
            if (response.data.success) {
                console.log('SUCCESS: Banking verification result accepted');
                console.log('Checkbox updated:', response.data.checkbox_updated);
                console.log('Checkbox state changed:', response.data.checkbox_state_changed);
                console.log('New checkbox state:', response.data.new_checkbox_state);
                console.log('Verification status:', response.data.verification_status);
                
                // Handle checkbox updates based on verification result
                if (response.data.checkbox_updated && response.data.checkbox_state_changed) {
                    // Update the form data to reflect the checkbox change
                    handleInputChange('verified_banking_details', response.data.new_checkbox_state);
                    
                    if (response.data.verification_status === 'VERIFIED') {
                        showSuccess('Banking verification VERIFIED and checkbox checked!');
                    } else if (response.data.verification_status === 'UNVERIFIED') {
                        showSuccess('Banking verification UNVERIFIED accepted and checkbox unchecked');
                    } else {
                        showSuccess('Banking verification accepted and checkbox updated!');
                    }
                } else if (response.data.checkbox_updated && !response.data.checkbox_state_changed) {
                    // Checkbox was "updated" but state didn't change (already in correct state)
                    if (response.data.verification_status === 'VERIFIED') {
                        showSuccess('Banking verification VERIFIED accepted (checkbox was already checked)');
                    } else if (response.data.verification_status === 'UNVERIFIED') {
                        showSuccess('Banking verification UNVERIFIED accepted (checkbox was already unchecked)');
                    } else {
                        showSuccess('Banking verification result accepted');
                    }
                } else {
                    showSuccess('Banking verification result accepted');
                }
                
                // Refresh audit logs to show acceptance
                await fetchAuditLogs();
                
                // Close modal
                setShowVerificationModal(false);
                setVerificationAnalysisData(null);
            }
        } catch (error) {
            console.error('ERROR: Error accepting verification result:', error);
            showError(`Failed to accept verification result: ${error.response?.data?.error || error.message}`);
        }
    };

    const declineVerificationResult = async () => {
        if (!verificationAnalysisData?.analysis_id) return;
        
        try {
            const response = await axiosInstance.post(
                `/api/crm/partners/${profileData.id}/banking-verification/${verificationAnalysisData.analysis_id}/decline/`
            );
            
            if (response.data.success) {
                console.log('SUCCESS: Banking verification result declined');
                showSuccess('Banking verification result declined');
                
                // Refresh audit logs to show decline
                await fetchAuditLogs();
                
                // Close modal
                setShowVerificationModal(false);
                setVerificationAnalysisData(null);
            }
        } catch (error) {
            console.error('ERROR: Error declining verification result:', error);
            showError(`Failed to decline verification result: ${error.response?.data?.error || error.message}`);
        }
    };

    // Manual verification handler - only updates local state
    const handleManualVerificationChange = (status) => {
        console.log('🔍 FRONTEND: Updating manual verification status (local only):', status);
        
        // Update form data locally only
        const updatedData = {
            ...formData,
            manual_bank_verification_status: status
        };
        
        setFormData(updatedData);
        onDataChange('manual_bank_verification_status', status);
        
        console.log('✅ Manual verification status updated locally. Use SAVE PROFILE to persist changes.');
    };

    // Deactivation handler - only updates local state
    const handleDeactivationChange = (isActive) => {
        console.log('🗄️ FRONTEND: Updating banking deactivation status (local only):', isActive);
        
        // Update form data locally only
        const updatedData = {
            ...formData,
            banking_details_active: isActive
        };
        
        setFormData(updatedData);
        onDataChange('banking_details_active', isActive);
        
        console.log('✅ Banking deactivation status updated locally. Use SAVE PROFILE to persist changes.');
    };

    // Fetch audit logs
    const fetchAuditLogs = async () => {
        if (!profileData?.id) return;

        setLoadingAuditLogs(true);
        try {
            console.log('📋 FRONTEND: Fetching banking audit logs for partner:', profileData.id);
            
            const response = await axiosInstance.get(`/api/crm/partners/${profileData.id}/banking/audit-logs/`);

            if (response.data.success) {
                console.log('📋 FRONTEND: Successfully fetched audit logs:', response.data.audit_logs);
                setAuditLogs(response.data.audit_logs || []);
            } else {
                console.error('📋 FRONTEND: Failed to fetch audit logs:', response.data.error);
                showError('Failed to fetch audit logs');
            }
        } catch (error) {
            console.error('📋 FRONTEND: Error fetching audit logs:', error);
            showError('Failed to fetch audit logs');
        } finally {
            setLoadingAuditLogs(false);
        }
    };

    // AI Analysis Modal handlers
    const handleViewAIAnalysis = (auditLog) => {
        console.log('Opening AI Analysis Modal for audit log:', auditLog);
        
        // Check if this is an initial analysis (not already accepted/declined)
        const isInitialAnalysis = auditLog.status === 'AI_ANALYSIS_VERIFIED' || auditLog.status === 'AI_ANALYSIS_UNVERIFIED';
        
        // Check if this specific analysis_id has already been acted upon
        const analysisId = auditLog.ai_analysis_data?.analysis_id;
        const hasBeenActioned = analysisId ? auditLogs.some(log => 
            (log.status === 'AI_ANALYSIS_ACCEPTED' || log.status === 'AI_ANALYSIS_DECLINED') &&
            log.ai_analysis_data?.analysis_id === analysisId
        ) : false;
        
        // Only allow actions if it's an initial analysis AND hasn't been acted upon yet
        const canTakeAction = isInitialAnalysis && !hasBeenActioned;
        
        console.log('Analysis action check:', {
            isInitialAnalysis,
            hasBeenActioned,
            canTakeAction,
            analysisId
        });
        
        setSelectedAIAnalysis({
            ...auditLog.ai_analysis_data,
            canTakeAction: canTakeAction,
            partner_name: profileData.partner_name || profileData.name
        });
        setShowAIAnalysisModal(true);
    };

    const handleCloseAIAnalysisModal = () => {
        setShowAIAnalysisModal(false);
        setSelectedAIAnalysis(null);
    };

    // Handle accept from AI Analysis Modal
    const handleAcceptFromModal = async (analysisId) => {
        if (!analysisId) return;
        
        try {
            const response = await axiosInstance.post(
                `/api/crm/partners/${profileData.id}/banking-verification/${analysisId}/accept/`
            );
            
            if (response.data.success) {
                showSuccess('AI verification accepted and banking details updated');
                
                // Refresh audit logs and profile data
                await fetchAuditLogs();
                
                // Update the profile data with new banking details
                if (response.data.updated_banking_details) {
                    const updatedFormData = {
                        ...formData,
                        banking_details_profiles: response.data.updated_banking_details
                    };
                    setFormData(updatedFormData);
                    
                    if (onDataChange) {
                        onDataChange(updatedFormData);
                    }
                }
                
                // Close the modal
                handleCloseAIAnalysisModal();
            }
        } catch (error) {
            console.error('ERROR: Error accepting verification result:', error);
            showError(`Failed to accept verification result: ${error.response?.data?.error || error.message}`);
        }
    };

    // Handle decline from AI Analysis Modal
    const handleDeclineFromModal = async (analysisId) => {
        if (!analysisId) return;
        
        try {
            const response = await axiosInstance.post(
                `/api/crm/partners/${profileData.id}/banking-verification/${analysisId}/decline/`
            );
            
            if (response.data.success) {
                showSuccess('AI verification declined');
                
                // Refresh audit logs
                await fetchAuditLogs();
                
                // Close the modal
                handleCloseAIAnalysisModal();
            }
        } catch (error) {
            console.error('ERROR: Error declining verification result:', error);
            showError(`Failed to decline verification result: ${error.response?.data?.error || error.message}`);
        }
    };

    // Component to display individual document with employee info and selection checkbox
    const DocumentInfoItem = ({ document }) => {
        const [employeeInfo, setEmployeeInfo] = useState(null);
        const [loadingEmployee, setLoadingEmployee] = useState(false);
        
        const docInfo = getDocumentInfo(document);
        
        // Create file object for selection
        const fileForSelection = {
            document_id: docInfo.documentId,
            filename: docInfo.filename
        };
        
        const isSelected = selectedFiles.some(f => f.document_id === docInfo.documentId);
        
        // Fetch employee info when component mounts or employeeId changes
        useEffect(() => {
            const fetchEmployeeInfo = async () => {
                if (!docInfo.employeeId) return;
                
                setLoadingEmployee(true);
                try {
                    const empInfo = await getEmployeeInfoByUserId(docInfo.employeeId);
                    setEmployeeInfo(empInfo);
                } catch (error) {
                    console.error('Error fetching employee info for document:', error);
                } finally {
                    setLoadingEmployee(false);
                }
            };
            
            fetchEmployeeInfo();
        }, [docInfo.employeeId]);
        
        const handleSelectionChange = (e) => {
            const checked = e.target.checked;
            if (checked) {
                setSelectedFiles(prev => [...prev, fileForSelection]);
            } else {
                setSelectedFiles(prev => prev.filter(f => f.document_id !== docInfo.documentId));
            }
        };
        
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.75rem',
                marginBottom: '0.5rem',
                backgroundColor: isSelected ? '#f0f9ff' : '#f8f9fa',
                borderRadius: '6px',
                border: isSelected ? '2px solid #3b82f6' : '1px solid #e9ecef'
            }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1 }}>
                    {/* Selection checkbox */}
                    <input
                        type="checkbox"
                        className="crm-banking-file-checkbox"
                        checked={isSelected}
                        onChange={handleSelectionChange}
                    />
                    <div style={{ flex: 1 }}>
                    <div style={{
                        fontWeight: 'bold',
                        color: '#495057',
                        marginBottom: '0.25rem',
                        fontSize: '0.95rem'
                    }} title={docInfo.displayName || docInfo.filename}>
                        {docInfo.displayName || docInfo.filename}
                    </div>
                    <SuggestedName row={docInfo.nameRow} onChanged={docInfo.onNameRow} />
                    {docInfo.aiDisplayName && docInfo.aiDisplayName !== docInfo.filename && (
                        <div style={{
                            fontSize: '0.75rem',
                            color: '#888',
                            marginBottom: '0.25rem',
                            wordBreak: 'break-all'
                        }} title={docInfo.filename}>
                            {docInfo.filename}
                        </div>
                    )}
                    <div style={{ 
                        fontSize: '0.85rem', 
                        color: '#6c757d',
                        marginBottom: '0.25rem'
                    }}>
                        Uploaded: {formatDate(docInfo.dateUploaded)} | Size: {formatFileSize(docInfo.fileSize)}
                    </div>
                    
                    {/* Employee Information Section */}
                    <div style={{ 
                        fontSize: '0.85rem', 
                        color: '#495057',
                        marginTop: '0.5rem',
                        padding: '0.4rem 0.6rem',
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                        border: '1px solid #dee2e6'
                    }}>
                        <strong>Uploaded by:</strong>
                        {loadingEmployee ? (
                            <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>Loading...</span>
                        ) : employeeInfo ? (
                            <div style={{ marginTop: '0.25rem' }}>
                                <div><strong>{employeeInfo.first_name} {employeeInfo.last_name}</strong></div>
                                {employeeInfo.position && (
                                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                        {employeeInfo.position}
                                        {employeeInfo.department && ` • ${employeeInfo.department}`}
                                        {employeeInfo.office && ` • ${employeeInfo.office}`}
                                    </div>
                                )}
                                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                    Employee ID: {employeeInfo.employee_id}
                                </div>
                            </div>
                        ) : (
                            <span style={{ marginLeft: '0.5rem', fontStyle: 'italic', color: '#6c757d' }}>
                                Employee info not available
                            </span>
                        )}
                    </div>
                    
                    <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#6c757d',
                        marginTop: '0.25rem'
                    }}>
                        Document ID: {docInfo.documentId}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                    <button
                        onClick={() => downloadDocument(docInfo.documentId, docInfo.filename)}
                        style={{
                            background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            minWidth: '80px',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                        }}
                    >
                        Download
                    </button>
                    <button
                        onClick={() => {
                            setDocumentToDelete({
                                id: docInfo.documentId,
                                filename: docInfo.filename
                            });
                            setShowDeleteConfirmation(true);
                        }}
                        style={{
                            background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            minWidth: '80px',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 4px rgba(220, 38, 38, 0.15)'
                        }}
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
        );
    };

    // Handle outside clicks for dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (paymentDebtorRef.current && !paymentDebtorRef.current.contains(event.target)) {
                setShowPaymentDebtorDropdown(false);
            }
            if (paymentCreditorRef.current && !paymentCreditorRef.current.contains(event.target)) {
                setShowPaymentCreditorDropdown(false);
            }
            if (vatTermsRef.current && !vatTermsRef.current.contains(event.target)) {
                setShowVATDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="crm-tab-panel crm-tab-modern">
            <CRMTabHero
                tone="emerald"
                icon={<TabIcons.Wallet />}
                title="Banking Details & Verification"
                description="Bank accounts, payment terms, tax information, and verification history."
            />

            {/* Banking Details Status Section - Moved to Top */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.ShieldCheck />}
                    iconTone="emerald"
                    title="Banking Details Status"
                />
                <div style={{
                    padding: '1rem 1.25rem',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    marginBottom: '1.5rem'
                }}>
                    <div className="crm-checkbox-group" style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <input
                                type="checkbox"
                                id="banking-active-checkbox"
                                className="crm-checkbox"
                                checked={formData.banking_details_active !== false}
                                onChange={(e) => handleDeactivationChange(e.target.checked)}
                                style={{
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '4px',
                                    border: '2px solid #d1d5db',
                                    transition: 'all 0.2s'
                                }}
                            />
                            <label htmlFor="banking-active-checkbox" className="crm-checkbox-label" style={{
                                fontSize: '0.95rem',
                                fontWeight: '500',
                                color: '#374151'
                            }}>
                                Banking details are active
                            </label>
                        </div>
                    </div>
                    
                    {formData.banking_details_active === false && (
                        <div style={{
                            padding: '1rem',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '8px',
                            marginTop: '1rem'
                        }}>
                            <div className="crm-input-group full-width">
                                <label className="crm-input-label" style={{ color: '#dc2626', fontWeight: '600' }}>
                                    Deactivation Reason
                                </label>
                                <textarea
                                    className="crm-input"
                                    value={formData.deactivation_reason || ''}
                                    onChange={(e) => handleInputChange('deactivation_reason', e.target.value)}
                                    placeholder="Reason for deactivating banking details..."
                                    rows={3}
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid #fca5a5',
                                        padding: '0.75rem',
                                        fontSize: '0.875rem',
                                        backgroundColor: 'white',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                            
                            {/* Deactivation Audit Trail */}
                            {formData.deactivated_by && (
                                <div style={{
                                    marginTop: '0.75rem',
                                    padding: '0.75rem 1rem',
                                    backgroundColor: '#fff1f2',
                                    border: '1px solid #fda4af',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <strong style={{ color: '#dc2626' }}>Deactivated by:</strong>
                                            <span style={{ marginLeft: '0.5rem', color: '#374151' }}>
                                                {formData.deactivated_by_name || 'Unknown User'}
                                            </span>
                                        </div>
                                        <div>
                                            <strong style={{ color: '#dc2626' }}>Date:</strong>
                                            <span style={{ marginLeft: '0.5rem', color: '#374151' }}>
                                                {formatDate(formData.deactivation_date)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Premium Audit Log Table */}
                    <div style={{ marginTop: '1rem' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '0.5rem'
                        }}>
                            <h4 style={{
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                color: '#374151',
                                margin: 0
                            }}>
                                Audit Trail
                            </h4>
                            {loadingAuditLogs && (
                                <div style={{
                                    fontSize: '0.75rem',
                                    color: '#6b7280',
                                    fontStyle: 'italic'
                                }}>
                                    Loading...
                                </div>
                            )}
                        </div>
                        
                        {auditLogs.length > 0 ? (
                            <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                backgroundColor: '#ffffff',
                                maxHeight: '300px',
                                overflowY: 'auto'
                            }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: '0.8rem'
                                }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f9fafb' }}>
                                            <th style={{
                                                padding: '0.5rem 0.75rem',
                                                textAlign: 'left',
                                                fontWeight: '600',
                                                color: '#374151',
                                                borderBottom: '1px solid #e5e7eb',
                                                fontSize: '0.75rem'
                                            }}>
                                                Type
                                            </th>
                                            <th style={{
                                                padding: '0.5rem 0.75rem',
                                                textAlign: 'left',
                                                fontWeight: '600',
                                                color: '#374151',
                                                borderBottom: '1px solid #e5e7eb',
                                                fontSize: '0.75rem'
                                            }}>
                                                Action
                                            </th>
                                            <th style={{
                                                padding: '0.5rem 0.75rem',
                                                textAlign: 'left',
                                                fontWeight: '600',
                                                color: '#374151',
                                                borderBottom: '1px solid #e5e7eb',
                                                fontSize: '0.75rem'
                                            }}>
                                                User
                                            </th>
                                            <th style={{
                                                padding: '0.5rem 0.75rem',
                                                textAlign: 'left',
                                                fontWeight: '600',
                                                color: '#374151',
                                                borderBottom: '1px solid #e5e7eb',
                                                fontSize: '0.75rem'
                                            }}>
                                                Date
                                            </th>
                                            <th style={{
                                                padding: '0.5rem 0.75rem',
                                                textAlign: 'left',
                                                fontWeight: '600',
                                                color: '#374151',
                                                borderBottom: '1px solid #e5e7eb',
                                                fontSize: '0.75rem'
                                            }}>
                                                Notes
                                            </th>
                                            <th style={{
                                                padding: '0.5rem 0.75rem',
                                                textAlign: 'center',
                                                fontWeight: '600',
                                                color: '#374151',
                                                borderBottom: '1px solid #e5e7eb',
                                                fontSize: '0.75rem',
                                                width: '80px'
                                            }}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auditLogs.map((log, index) => (
                                            <tr key={index} style={{
                                                borderBottom: index < auditLogs.length - 1 ? '1px solid #f3f4f6' : 'none'
                                            }}>
                                                <td style={{
                                                    padding: '0.5rem 0.75rem',
                                                    color: '#374151',
                                                    fontSize: '0.75rem'
                                                }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '0.125rem 0.375rem',
                                                        borderRadius: '4px',
                                                        fontSize: '0.6875rem',
                                                        fontWeight: '500',
                                                        backgroundColor: log.type === 'Manual Verification' ? '#dbeafe' : '#fef3c7',
                                                        color: log.type === 'Manual Verification' ? '#1e40af' : '#92400e'
                                                    }}>
                                                        {log.type}
                                                    </span>
                                                </td>
                                                <td style={{
                                                    padding: '0.5rem 0.75rem',
                                                    color: '#374151',
                                                    fontSize: '0.75rem'
                                                }}>
                                                    {log.action}
                                                </td>
                                                <td style={{
                                                    padding: '0.5rem 0.75rem',
                                                    color: '#374151',
                                                    fontSize: '0.75rem'
                                                }}>
                                                    {log.user ? (
                                                        <div>
                                                            <div style={{ fontWeight: '500' }}>
                                                                {`${log.user.first_name || ''} ${log.user.last_name || ''}`.trim() || log.user.email}
                                                            </div>
                                                            {log.user.first_name && (
                                                                <div style={{ 
                                                                    fontSize: '0.6875rem', 
                                                                    color: '#6b7280',
                                                                    marginTop: '0.125rem'
                                                                }}>
                                                                    {log.user.email}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                                            Unknown
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{
                                                    padding: '0.5rem 0.75rem',
                                                    color: '#374151',
                                                    fontSize: '0.75rem'
                                                }}>
                                                    {new Date(log.date).toLocaleString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </td>
                                                <td style={{
                                                    padding: '0.5rem 0.75rem',
                                                    color: '#374151',
                                                    fontSize: '0.75rem',
                                                    maxWidth: '200px',
                                                    wordWrap: 'break-word'
                                                }}>
                                                    {log.notes || (
                                                        <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                                                            No notes
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{
                                                    padding: '0.5rem 0.75rem',
                                                    textAlign: 'center'
                                                }}>
                                                    {log.has_ai_analysis && (log.status === 'AI_ANALYSIS_VERIFIED' || log.status === 'AI_ANALYSIS_UNVERIFIED') && (
                                                        <button
                                                            onClick={() => handleViewAIAnalysis(log)}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: '#4a6b25',
                                                                cursor: 'pointer',
                                                                padding: '0.25rem 0.5rem',
                                                                fontSize: '1rem',
                                                                transition: 'color 0.2s',
                                                                borderRadius: '4px'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.color = '#3a5b15';
                                                                e.currentTarget.style.backgroundColor = '#f0f9ff';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.color = '#4a6b25';
                                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                            }}
                                                            title="View AI Analysis Details"
                                                        >
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{
                                padding: '1rem',
                                textAlign: 'center',
                                color: '#6b7280',
                                fontSize: '0.75rem',
                                fontStyle: 'italic',
                                backgroundColor: '#f9fafb',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px'
                            }}>
                                No audit logs available
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Banking Details Section */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.Wallet />}
                    iconTone="blue"
                    title="Banking Details"
                />
                
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '1.5rem' 
                }}>
                    <p style={{ 
                        margin: 0, 
                        color: '#6b7280', 
                        fontSize: '0.875rem' 
                    }}>
                        Add multiple bank account details for this partner
                    </p>
                    <button
                        type="button"
                        onClick={addBankDetails}
                        style={{
                            backgroundColor: '#4a6b25',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                        }}
                    >
                        + Add Bank Details
                    </button>
                </div>

                {formData.banking_details_profiles?.map((bank, index) => (
                    <div key={bank.id} style={{ 
                        marginBottom: '2rem', 
                        padding: '1.5rem', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '0.5rem',
                        backgroundColor: '#f9fafb'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ fontWeight: '600', color: '#374151', margin: 0 }}>
                                Bank Details {index + 1}
                                {/* Show saved indicator for saved profiles */}
                                {bank.database_id || (typeof bank.id === 'number' && bank.id.toString().length < 13) ? (
                                    <span style={{
                                        marginLeft: '0.5rem',
                                        padding: '0.25rem 0.5rem',
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        borderRadius: '12px',
                                        fontSize: '0.75rem',
                                        fontWeight: '500'
                                    }}>
                                        Saved
                                    </span>
                                ) : (
                                    <span style={{
                                        marginLeft: '0.5rem',
                                        padding: '0.25rem 0.5rem',
                                        backgroundColor: '#f59e0b',
                                        color: 'white',
                                        borderRadius: '12px',
                                        fontSize: '0.75rem',
                                        fontWeight: '500'
                                    }}>
                                        New
                                    </span>
                                )}
                            </h3>
                            {(() => {
                                // Determine if this is a saved profile
                                const isSavedProfile = bank.database_id || (typeof bank.id === 'number' && bank.id.toString().length < 13);
                                
                                if (isSavedProfile) {
                                    // Saved profiles: Show archive message instead of delete button
                                    return (
                                        <div style={{
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            fontStyle: 'italic',
                                            textAlign: 'right',
                                            maxWidth: '200px'
                                        }}>
                                            Use "Banking Details Status" above to archive
                                        </div>
                                    );
                                } else {
                                    // New profiles: Show delete button
                                    return (
                                        <button
                                            type="button"
                                            onClick={() => removeBankDetails(bank.id)}
                                            style={{
                                                color: '#ef4444',
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '1.25rem',
                                                padding: '0.25rem'
                                            }}
                                            title="Remove new bank details"
                                        >
                                            ×
                                        </button>
                                    );
                                }
                            })()}
                        </div>

                        {/* Account Owner - Required Field */}
                        <div className="crm-input-row">
                            <div className="crm-input-group full-width">
                                <label className="crm-input-label required">Account Owner</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={bank.account_owner || ''}
                                    onChange={(e) => handleBankDetailsChange(bank.id, 'account_owner', e.target.value)}
                                    placeholder="Legal name of account holder"
                                />
                            </div>
                        </div>

                        {/* Bank Name */}
                        <div className="crm-input-row">
                            <div className="crm-input-group full-width">
                                <label className="crm-input-label required">Bank Name</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={bank.bank_name || ''}
                                    onChange={(e) => handleBankDetailsChange(bank.id, 'bank_name', e.target.value)}
                                    placeholder="Name of the bank"
                                />
                            </div>
                        </div>

                        {/* Bank Address */}
                        <div className="crm-input-row">
                            <div className="crm-input-group full-width">
                                <label className="crm-input-label">Bank Address</label>
                                <AddressInput
                                    name={`bank_address_${bank.id}`}
                                    value={bank.bank_address_data?.formatted_address || ''}
                                    onChange={(e) => handleBankDetailsChange(bank.id, 'bank_address_data', {
                                        ...bank.bank_address_data,
                                        formatted_address: e.target.value
                                    })}
                                    onSelect={(suggestion) => handleBankAddressSelect(bank.id, suggestion)}
                                    placeholder="Start typing bank address..."
                                    className="crm-input"
                                    styleType="crm"
                                    rows={2}
                                />
                            </div>
                        </div>

                        {/* Account Details */}
                        <div className="crm-input-row">
                            <div className="crm-input-group quarter-width">
                                <label className="crm-input-label">Account Number</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={bank.account_number || ''}
                                    onChange={(e) => handleBankDetailsChange(bank.id, 'account_number', e.target.value)}
                                    placeholder="Account number"
                                />
                            </div>
                            <div className="crm-input-group quarter-width">
                                <label className="crm-input-label">IBAN</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={bank.iban || ''}
                                    onChange={(e) => handleBankDetailsChange(bank.id, 'iban', e.target.value)}
                                    placeholder="IBAN code"
                                />
                            </div>
                            <div className="crm-input-group quarter-width">
                                <label className="crm-input-label">SWIFT</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={bank.swift || ''}
                                    onChange={(e) => handleBankDetailsChange(bank.id, 'swift', e.target.value)}
                                    placeholder="SWIFT code"
                                />
                            </div>
                            <div className="crm-input-group quarter-width">
                                <label className="crm-input-label">BIC</label>
                                <input
                                    type="text"
                                    className="crm-input"
                                    value={bank.bic || ''}
                                    onChange={(e) => handleBankDetailsChange(bank.id, 'bic', e.target.value)}
                                    placeholder="BIC code"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Entity Currency Settings */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.CreditCard />}
                    iconTone="blue"
                    title="Entity Currency Settings"
                />
                <p className="crm-input-help" style={{ marginBottom: '1rem', fontStyle: 'normal' }}>
                    The base currency will be used as the default when creating invoices for this partner.
                </p>

                <div className="crm-form-grid-2">
                    <div className="crm-input-group">
                        <label className="crm-input-label">Base Currency</label>
                        <select
                            className="crm-input"
                            value={formData.base_currency || 'USD'}
                            onChange={(e) => handleInputChange('base_currency', e.target.value)}
                        >
                            {CURRENCY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="crm-input-group">
                        <label className="crm-input-label">Additional Currencies</label>
                        <select
                            className="crm-input"
                            value=""
                            onChange={(e) => {
                                const code = e.target.value;
                                if (!code || formData.additional_currencies?.includes(code) || code === formData.base_currency) return;
                                handleInputChange('additional_currencies', [...(formData.additional_currencies || []), code]);
                            }}
                        >
                            <option value="" disabled>Add a currency...</option>
                            {CURRENCY_OPTIONS
                                .filter(opt => opt.value !== formData.base_currency && !(formData.additional_currencies || []).includes(opt.value))
                                .map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))
                            }
                        </select>
                    </div>
                </div>

                {formData.additional_currencies?.length > 0 && (
                    <div className="crm-selected-tags" style={{ marginTop: '0.5rem' }}>
                        {formData.additional_currencies.map(code => {
                            const opt = CURRENCY_OPTIONS.find(o => o.value === code);
                            return (
                                <div key={code} className="crm-tag">
                                    {opt ? opt.label : code}
                                    <button
                                        type="button"
                                        className="crm-tag-remove"
                                        onClick={() => handleInputChange('additional_currencies', formData.additional_currencies.filter(c => c !== code))}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Payment Terms Section */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.Calendar />}
                    iconTone="indigo"
                    title="Payment Terms"
                />
                
                {/* Payment Terms as Debtor */}
                <div className="crm-input-row">
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Payment Terms as Debtor</label>
                        <div className="crm-modern-multi-select-container" ref={paymentDebtorRef}>
                            <div 
                                className={`crm-modern-multi-select-header ${showPaymentDebtorDropdown ? 'active' : ''}`}
                                onClick={() => setShowPaymentDebtorDropdown(!showPaymentDebtorDropdown)}
                            >
                                <span>
                                    {formData.payment_terms_debtor_selections?.length === 0 
                                        ? 'Select payment terms as debtor' 
                                        : `${formData.payment_terms_debtor_selections.length} selected`}
                                </span>
                            </div>
                            
                            {showPaymentDebtorDropdown && (
                                <div className="crm-modern-multi-select-dropdown">
                                    <div className="crm-modern-multi-select-actions">
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('payment_terms_debtor_selections', paymentTermsOptions.map((o) => o.value));
                                            }}
                                        >
                                            Select All
                                        </button>
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('payment_terms_debtor_selections', []);
                                            }}
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                    
                                    <div className="crm-modern-multi-select-options">
                                        {paymentTermsLoadFailed && (
                                            <div className="crm-modern-multi-select-option">
                                                Payment terms could not be loaded. Refresh, or ask an admin to check your CRM access.
                                            </div>
                                        )}
                                        {paymentTermsOptions.map((option) => (
                                            <div key={option.value} className="crm-modern-multi-select-option">
                                                <label className="crm-modern-checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.payment_terms_debtor_selections?.includes(option.value) || false}
                                                        onChange={() => handleMultiSelectToggle('payment_terms_debtor_selections', option.value)}
                                                    />
                                                    <span className="crm-modern-checkbox-checkmark"></span>
                                                    {option.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {formData.payment_terms_debtor_selections?.length > 0 && (
                            <div className="crm-selected-tags">
                                {formData.payment_terms_debtor_selections.map((tag) => (
                                    <div key={tag} className="crm-tag">
                                        {paymentTermLabel(tag, paymentTermsOptions)}
                                        <button
                                            type="button"
                                            className="crm-tag-remove"
                                            onClick={() => removeTag('payment_terms_debtor_selections', tag)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Payment Terms as Creditor */}
                    <div className="crm-input-group half-width">
                        <label className="crm-input-label">Payment Terms as Creditor</label>
                        <div className="crm-modern-multi-select-container" ref={paymentCreditorRef}>
                            <div 
                                className={`crm-modern-multi-select-header ${showPaymentCreditorDropdown ? 'active' : ''}`}
                                onClick={() => setShowPaymentCreditorDropdown(!showPaymentCreditorDropdown)}
                            >
                                <span>
                                    {formData.payment_terms_creditor_selections?.length === 0 
                                        ? 'Select payment terms as creditor' 
                                        : `${formData.payment_terms_creditor_selections.length} selected`}
                                </span>
                            </div>
                            
                            {showPaymentCreditorDropdown && (
                                <div className="crm-modern-multi-select-dropdown">
                                    <div className="crm-modern-multi-select-actions">
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('payment_terms_creditor_selections', paymentTermsOptions.map((o) => o.value));
                                            }}
                                        >
                                            Select All
                                        </button>
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('payment_terms_creditor_selections', []);
                                            }}
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                    
                                    <div className="crm-modern-multi-select-options">
                                        {paymentTermsLoadFailed && (
                                            <div className="crm-modern-multi-select-option">
                                                Payment terms could not be loaded. Refresh, or ask an admin to check your CRM access.
                                            </div>
                                        )}
                                        {paymentTermsOptions.map((option) => (
                                            <div key={option.value} className="crm-modern-multi-select-option">
                                                <label className="crm-modern-checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.payment_terms_creditor_selections?.includes(option.value) || false}
                                                        onChange={() => handleMultiSelectToggle('payment_terms_creditor_selections', option.value)}
                                                    />
                                                    <span className="crm-modern-checkbox-checkmark"></span>
                                                    {option.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {formData.payment_terms_creditor_selections?.length > 0 && (
                            <div className="crm-selected-tags">
                                {formData.payment_terms_creditor_selections.map((tag) => (
                                    <div key={tag} className="crm-tag">
                                        {paymentTermLabel(tag, paymentTermsOptions)}
                                        <button
                                            type="button"
                                            className="crm-tag-remove"
                                            onClick={() => removeTag('payment_terms_creditor_selections', tag)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* VAT & Tax Information Section */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.Receipt />}
                    iconTone="amber"
                    title="VAT & Tax Information"
                />
                
                {/* VAT Terms */}
                <div className="crm-input-row">
                    <div className="crm-input-group full-width">
                        <label className="crm-input-label">VAT Terms</label>
                        <div className="crm-modern-multi-select-container" ref={vatTermsRef}>
                            <div 
                                className={`crm-modern-multi-select-header ${showVATDropdown ? 'active' : ''}`}
                                onClick={() => setShowVATDropdown(!showVATDropdown)}
                            >
                                <span>
                                    {formData.vat_terms_selections?.length === 0 
                                        ? 'Select VAT terms' 
                                        : `${formData.vat_terms_selections.length} selected`}
                                </span>
                            </div>
                            
                            {showVATDropdown && (
                                <div className="crm-modern-multi-select-dropdown">
                                    <div className="crm-modern-multi-select-actions">
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('vat_terms_selections', vatTermsOptions);
                                            }}
                                        >
                                            Select All
                                        </button>
                                        <button 
                                            type="button" 
                                            className="crm-modern-multi-select-action-btn"
                                            onClick={() => {
                                                handleInputChange('vat_terms_selections', []);
                                            }}
                                        >
                                            Deselect All
                                        </button>
                                    </div>
                                    
                                    <div className="crm-modern-multi-select-options">
                                        {vatTermsOptions.map((option) => (
                                            <div key={option} className="crm-modern-multi-select-option">
                                                <label className="crm-modern-checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.vat_terms_selections?.includes(option) || false}
                                                        onChange={() => handleMultiSelectToggle('vat_terms_selections', option)}
                                                    />
                                                    <span className="crm-modern-checkbox-checkmark"></span>
                                                    {option}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {formData.vat_terms_selections?.length > 0 && (
                            <div className="crm-selected-tags">
                                {formData.vat_terms_selections.map((tag) => (
                                    <div key={tag} className="crm-tag">
                                        {tag}
                                        <button
                                            type="button"
                                            className="crm-tag-remove"
                                            onClick={() => removeTag('vat_terms_selections', tag)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Tax Registration Numbers - Separate Fields */}
                {/* Tax Registration Numbers - Visual Group with 2x2 Grid */}
                <div className="crm-section-group">
                    <h3 className="crm-section-subtitle">Tax Registration Numbers</h3>
                    <div className="crm-form-grid-2">
                        <div className="crm-input-group">
                            <label className="crm-input-label">UID Registration Number</label>
                            <input
                                type="text"
                                className="crm-input"
                                value={formData.uid_registration_number}
                                onChange={(e) => handleInputChange('uid_registration_number', e.target.value)}
                                placeholder="UID number"
                            />
                        </div>
                        <div className="crm-input-group">
                            <label className="crm-input-label">LEI Registration Number</label>
                            <input
                                type="text"
                                className="crm-input"
                                value={formData.lei_registration_number}
                                onChange={(e) => handleInputChange('lei_registration_number', e.target.value)}
                                placeholder="LEI number"
                            />
                        </div>
                        <div className="crm-input-group">
                            <label className="crm-input-label">LIC Registration Number</label>
                            <input
                                type="text"
                                className="crm-input"
                                value={formData.lic_registration_number}
                                onChange={(e) => handleInputChange('lic_registration_number', e.target.value)}
                                placeholder="LIC number"
                            />
                        </div>
                        <div className="crm-input-group">
                            <label className="crm-input-label">VAT Registration Number</label>
                            <input
                                type="text"
                                className="crm-input"
                                value={formData.vat_registration_number}
                                onChange={(e) => handleInputChange('vat_registration_number', e.target.value)}
                                placeholder="VAT number"
                            />
                        </div>
                        <div className="crm-input-group">
                            <label className="crm-input-label">Registration Number</label>
                            <input
                                type="text"
                                className="crm-input"
                                value={formData.tax_registration_numbers?.reg || ''}
                                onChange={(e) => handleInputChange('tax_registration_numbers', {
                                    ...(formData.tax_registration_numbers || {}),
                                    reg: e.target.value
                                })}
                                placeholder="Company registration number"
                            />
                        </div>
                        <div className="crm-input-group">
                            <label className="crm-input-label">DUNS Number</label>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={11}
                                    className="crm-input"
                                    value={formData.duns_registration_number || ''}
                                    onChange={(e) => handleInputChange('duns_registration_number', e.target.value.replace(/[^0-9-]/g, ''))}
                                    placeholder="9-digit D-U-N-S Number"
                                    style={{ flex: 1 }}
                                />
                                <button
                                    type="button"
                                    className="crm-action-btn"
                                    onClick={() => {
                                        const companyName = (formData.legal_entity_name || profileData?.legal_entity_name || '').trim();
                                        if (!companyName) {
                                            showError('Enter the legal entity name first to look up the DUNS Number.');
                                            return;
                                        }
                                        // Soft automation: open ClarityCheck reverse-entity-lookup and copy
                                        // the company name to clipboard so the user can paste straight in.
                                        const lookupUrl = 'https://claritycheck.com/reverse-entity-lookup';
                                        try {
                                            if (navigator.clipboard && navigator.clipboard.writeText) {
                                                navigator.clipboard.writeText(companyName);
                                            }
                                        } catch (err) {
                                            // Clipboard access can be blocked; non-fatal.
                                        }
                                        window.open(lookupUrl, '_blank', 'noopener,noreferrer');
                                        showSuccess(`Opened ClarityCheck. "${companyName}" copied to clipboard — paste into the search field.`, 5000);
                                    }}
                                    title="Open ClarityCheck and copy the company name to clipboard for DUNS lookup"
                                    style={{
                                        padding: '0 1rem',
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faSearch} />
                                    Lookup
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Manual Banking Verification Section */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.UserCheck />}
                    iconTone="emerald"
                    title="Manual Banking Verification"
                />
                <div style={{
                    padding: '1rem 1.25rem',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    marginBottom: '1.5rem'
                }}>
                    
                    <div className="crm-input-row">
                        <div className="crm-input-group half-width">
                            <label className="crm-input-label">Verification Status</label>
                            <select
                                className="crm-input"
                                value={formData.manual_bank_verification_status || 'PENDING'}
                                onChange={(e) => handleManualVerificationChange(e.target.value)}
                                style={{
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0',
                                    padding: '0.875rem 1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '500',
                                    color: '#374151',
                                    backgroundColor: 'white',
                                    transition: 'all 0.2s ease-in-out',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    appearance: 'none',
                                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 0.875rem center',
                                    backgroundSize: '16px',
                                    paddingRight: '2.5rem'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#4a6b25';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(74, 107, 37, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.boxShadow = 'none';
                                }}
                                onMouseEnter={(e) => {
                                    if (e.target !== document.activeElement) {
                                        e.target.style.borderColor = '#cbd5e1';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (e.target !== document.activeElement) {
                                        e.target.style.borderColor = '#e2e8f0';
                                    }
                                }}
                            >
                                <option value="PENDING">Pending Verification</option>
                                <option value="DONE">Verification Complete</option>
                                <option value="FIXED">Issues Fixed</option>
                                <option value="INVALID">Invalid Details</option>
                            </select>
                        </div>
                        
                        <div className="crm-input-group half-width">
                            <label className="crm-input-label">Verification Notes</label>
                            <textarea
                                className="crm-input"
                                value={formData.manual_verification_notes || ''}
                                onChange={(e) => handleInputChange('manual_verification_notes', e.target.value)}
                                placeholder="Add verification notes..."
                                rows={3}
                                style={{
                                    borderRadius: '8px',
                                    border: '1px solid #d1d5db',
                                    padding: '0.75rem',
                                    fontSize: '0.875rem',
                                    transition: 'all 0.2s',
                                    resize: 'vertical',
                                    backgroundColor: 'white'
                                }}
                            />
                        </div>
                    </div>
                    
                    {/* Audit Trail Display */}
                    {formData.manual_verification_user && (
                        <div style={{
                            marginTop: '1rem',
                            padding: '0.75rem 1rem',
                            backgroundColor: '#f0f9ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '8px',
                            fontSize: '0.875rem'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <strong style={{ color: '#1e40af' }}>Verified by:</strong>
                                    <span style={{ marginLeft: '0.5rem', color: '#374151' }}>
                                        {formData.manual_verification_user_name || 'Unknown User'}
                                    </span>
                                </div>
                                <div>
                                    <strong style={{ color: '#1e40af' }}>Date:</strong>
                                    <span style={{ marginLeft: '0.5rem', color: '#374151' }}>
                                        {formatDate(formData.manual_verification_date)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* AI Verification Section */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.Sparkle />}
                    iconTone="violet"
                    title="AI Verification"
                />
                
                {/* Document Management Section - V2 DocuWare Integration */}
                <div className="crm-input-row">
                    <div className="crm-input-group full-width">
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: '30px' 
                        }}>
                            <div>
                                <label className="crm-input-label">Banking Verification Documents (auto-loaded)</label>
                                <div style={{ 
                                    fontSize: '0.75rem', 
                                    color: '#6b7280', 
                                    marginTop: '0.25rem' 
                                }}>
                                    Documents are automatically loaded when you open this tab
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!showDocuments) {
                                            setShowDocuments(true);
                                            if (profileData?.id) {
                                                fetchBankingDocuments();
                                            }
                                        } else {
                                            setShowDocuments(false);
                                        }
                                    }}
                                    style={{
                                        background: 'linear-gradient(135deg, #4a6b25, #3a5b15)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.5rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 2px 4px rgba(74, 107, 37, 0.15)'
                                    }}
                                >
                                    {showDocuments ? 'Hide Documents' : 'View Documents'}
                                </button>
                                {showDocuments && (
                                    <button
                                        type="button"
                                        onClick={fetchBankingDocuments}
                                        disabled={loadingDocuments}
                                        style={{
                                            background: loadingDocuments ? '#6c757d' : 'linear-gradient(135deg, #5a7b35, #4a6b25)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem 1rem',
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            cursor: loadingDocuments ? 'not-allowed' : 'pointer',
                                            opacity: loadingDocuments ? 0.6 : 1,
                                            transition: 'all 0.2s',
                                            boxShadow: loadingDocuments ? 'none' : '0 2px 4px rgba(74, 107, 37, 0.15)'
                                        }}
                                    >
                                        {loadingDocuments ? 'Refreshing...' : '↻ Refresh'}
                                    </button>
                                )}
                                {/* Upload button - always visible but disabled when profile not saved */}
                                <div style={{ position: 'relative' }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowUploadModal(true)}
                                        disabled={uploading || isNewProfile || !profileData?.id}
                                        style={{
                                            background: (isNewProfile || !profileData?.id || uploading) 
                                                ? '#6c757d' 
                                                : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem 1rem',
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            cursor: (isNewProfile || !profileData?.id || uploading) ? 'not-allowed' : 'pointer',
                                            opacity: (isNewProfile || !profileData?.id || uploading) ? 0.6 : 1,
                                            transition: 'all 0.2s',
                                            boxShadow: (isNewProfile || !profileData?.id || uploading) 
                                                ? 'none' 
                                                : '0 2px 4px rgba(245, 158, 11, 0.15)'
                                        }}
                                    >
                                        {uploading ? 'Uploading...' : 'Upload Document'}
                                    </button>
                                    {(isNewProfile || !profileData?.id) && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: '0',
                                            right: '0',
                                            marginTop: '0.25rem',
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            fontStyle: 'italic',
                                            wordWrap: 'break-word',
                                            lineHeight: '1.2'
                                        }}>
                                            Save profile first to upload documents
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Document List Display */}
                        {showDocuments && (
                            <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '0.5rem',
                                backgroundColor: '#f9fafb',
                                marginBottom: '1rem'
                            }}>
                                {loadingDocuments && (
                                    <div style={{ 
                                        padding: '2rem', 
                                        textAlign: 'center',
                                        color: '#6b7280'
                                    }}>
                                        Loading documents...
                                    </div>
                                )}

                                {documentError && (
                                    <div style={{ 
                                        padding: '1rem',
                                        backgroundColor: '#fef2f2',
                                        border: '1px solid #ef4444',
                                        borderRadius: '0.375rem',
                                        color: '#dc2626',
                                        margin: '1rem'
                                    }}>
                                        Error: {documentError}
                                    </div>
                                )}

                                {!loadingDocuments && !documentError && documentList.length === 0 && (
                                    <div style={{ 
                                        padding: '2rem', 
                                        textAlign: 'center',
                                        color: '#6b7280'
                                    }}>
                                        No documents found for this partner
                                    </div>
                                )}

                                {!loadingDocuments && !documentError && documentList.length > 0 && (
                                    <div>
                                        <div style={{
                                            padding: '1rem',
                                            borderBottom: '1px solid #e5e7eb',
                                            backgroundColor: '#f3f4f6',
                                            fontWeight: '600',
                                            color: '#374151'
                                        }}>
                                            Found {documentList.length} document{documentList.length !== 1 ? 's' : ''}
                                        </div>
                                        {documentList.map((document) => (
                                            <DocumentInfoItem key={document.Id} document={document} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Delete Confirmation Popup */}
                {showDeleteConfirmation && documentToDelete && (
                    <ConfirmationPopup
                        message={`Are you sure you want to delete "${documentToDelete.filename}"? This action cannot be undone.`}
                        onConfirm={() => {
                            deleteDocument(documentToDelete.id, documentToDelete.filename);
                            setShowDeleteConfirmation(false);
                            setDocumentToDelete(null);
                        }}
                        onCancel={() => {
                            setShowDeleteConfirmation(false);
                            setDocumentToDelete(null);
                        }}
                    />
                )}

                {/* Duplicate Detection Modal */}
                {showDuplicateModal && duplicateFileInfo && (
                    <div className="confirmation-popup-overlay">
                        <div className="confirmation-popup">
                            <div className="confirmation-popup-message">
                                <h3 style={{ 
                                    margin: '0 0 1rem 0', 
                                    color: '#374151',
                                    fontSize: '1.25rem',
                                    fontWeight: '600'
                                }}>
                                    Duplicate File Detected
                                </h3>
                                <p style={{ margin: '0 0 1rem 0', color: '#6b7280' }}>
                                    A file with the name "<strong>{duplicateFileInfo.file.name}</strong>" already exists in this partner's banking documents.
                                </p>
                                <p style={{ margin: '0', color: '#374151', fontSize: '0.95rem' }}>
                                    What would you like to do?
                                </p>
                            </div>
                            <div className="confirmation-popup-buttons" style={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                gap: '0.75rem',
                                marginTop: '1.5rem',
                                flexWrap: 'wrap' 
                            }}>
                                <button 
                                    onClick={handleDuplicateKeepBoth}
                                    disabled={uploading}
                                    style={{
                                        background: uploading ? '#6c757d' : 'linear-gradient(135deg, #10b981, #059669)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '0.6rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: uploading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        minWidth: '90px'
                                    }}
                                >
                                    Keep Both
                                </button>
                                <button 
                                    onClick={handleDuplicateReplace}
                                    disabled={uploading}
                                    style={{
                                        background: uploading ? '#6c757d' : 'linear-gradient(135deg, #f59e0b, #d97706)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '0.6rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: uploading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        minWidth: '90px'
                                    }}
                                >
                                    Replace
                                </button>
                                <button 
                                    onClick={handleDuplicateCancel}
                                    disabled={uploading}
                                    style={{
                                        background: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '0.6rem 1rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: uploading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        minWidth: '90px'
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Banking Verification Modal */}
                <BankingVerificationModal
                    isOpen={showVerificationModal}
                    onClose={() => {
                        setShowVerificationModal(false);
                        setVerificationAnalysisData(null);
                    }}
                    analysisData={verificationAnalysisData}
                    onAccept={acceptVerificationResult}
                    onDecline={declineVerificationResult}
                />

                {/* Banking Upload Modal */}
                <BankingUploadModal
                    isOpen={showUploadModal}
                    onClose={() => setShowUploadModal(false)}
                    onDocumentSelect={handleModalFileUpload}
                    uploading={uploading}
                    profileId={profileData?.id}
                    partnerName={profileData?.legal_entity_name || ''}
                />

            </div>

            {/* Banking Verification Analysis History */}
            <div className="crm-form-section">
                <CRMSectionHeader
                    icon={<TabIcons.History />}
                    iconTone="indigo"
                    title="Banking Verification Analysis History"
                />
                
                {/* Info text */}
                <div style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '0.5rem',
                    marginBottom: '1.5rem',
                    fontSize: '0.875rem',
                    color: '#1e40af',
                    lineHeight: '1.5'
                }}>
                    <strong>How to use AI Banking Verification:</strong> First, upload your banking verification documents using the "Upload Document" button above. Then select the documents you want to analyze by checking the boxes next to them in the document list. Finally, click "AI Verify Banking" to run the analysis. The AI will validate banking details against uploaded documents and provide a verification result.
                </div>
                
                {/* Verification Controls */}
                <div className="crm-input-row" style={{ marginBottom: '2rem' }}>
                    <div className="crm-input-group full-width">
                        <div className="crm-checkbox-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                {/* Left side - Checkbox */}
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <input
                                        type="checkbox"
                                        id="verified-banking-checkbox"
                                        className="crm-checkbox"
                                        checked={formData.verified_banking_details || false}
                                        onChange={(e) => handleInputChange('verified_banking_details', e.target.checked)}
                                    />
                                    <label htmlFor="verified-banking-checkbox" className="crm-checkbox-label">
                                        Banking details have been verified
                                    </label>
                                </div>
                                
                                {/* Right side - AI Verification Button */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <button
                                        type="button"
                                        onClick={runBankingVerification}
                                        disabled={analyzingBanking || selectedFiles.length === 0 || isNewProfile || !profileData?.id}
                                        style={{
                                            background: (analyzingBanking || selectedFiles.length === 0 || isNewProfile || !profileData?.id) 
                                                ? '#6c757d' 
                                                : 'linear-gradient(135deg, #10b981, #059669)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '0.5rem 1rem',
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            cursor: (analyzingBanking || selectedFiles.length === 0 || isNewProfile || !profileData?.id) ? 'not-allowed' : 'pointer',
                                            opacity: (analyzingBanking || selectedFiles.length === 0 || isNewProfile || !profileData?.id) ? 0.6 : 1,
                                            transition: 'all 0.2s',
                                            boxShadow: (analyzingBanking || selectedFiles.length === 0 || isNewProfile || !profileData?.id) 
                                                ? 'none' 
                                                : '0 2px 4px rgba(16, 185, 129, 0.15)',
                                            minWidth: '140px'
                                        }}
                                    >
                                        {analyzingBanking ? 'Analyzing...' : 'AI Verify Banking'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <DocumentReadingRunPanel
                            run={bankingRun}
                            activeText="Reading the chosen documents and checking the banking details. Scanned documents can take several minutes; this updates by itself."
                            onDismiss={clearBankingRun}
                        />
                        
                        {/* Selected files summary */}
                        {selectedFiles.length > 0 && (
                            <div style={{
                                marginTop: '1rem',
                                padding: '1rem',
                                backgroundColor: '#f0f9ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: '8px'
                            }}>
                                <h4 style={{
                                    margin: '0 0 0.5rem 0',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    color: '#1e40af'
                                }}>
                                    Selected for Verification ({selectedFiles.length} document{selectedFiles.length !== 1 ? 's' : ''}):
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    {selectedFiles.map((file, index) => (
                                        <div key={index} style={{
                                            fontSize: '0.875rem',
                                            color: '#374151',
                                            paddingLeft: '0.5rem'
                                        }}>
                                            • {file.filename}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                    </div>
            
            {/* AI Analysis Modal */}
            <BankingAIAnalysisModal
                isOpen={showAIAnalysisModal}
                analysisData={selectedAIAnalysis}
                onClose={handleCloseAIAnalysisModal}
                onAccept={selectedAIAnalysis?.canTakeAction ? handleAcceptFromModal : null}
                onDecline={selectedAIAnalysis?.canTakeAction ? handleDeclineFromModal : null}
            />
        </div>
    );
};

export default CRMBankingDetailsTab; 